/**
 * @file MedicalAssessmentsPage.tsx
 * @brief Company Admin → Medical Assessment intake inbox + assessment list.
 *
 * @details
 * The manual route in (clinic-software picker, doctor directory, schedule,
 * Case Hub lookup by claimant id, preview, create-from-preview) was removed on
 * 13-09-2026. Partner software pushes the case with its `caseHub` and schedule
 * attached and the inbox receives it, so the lookup ladder was a control a
 * doctor had no reason to climb. Git history has it if a manual path is ever
 * needed again.
 *
 * @author DES
 * @date 02-09-2026
 * @updated 13-09-2026
 */

import { DockActionButton, PageDock, pageWithDockClass } from "@/components/ui/PageDock";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { LoaderCircle, Plus, FileHeart, Trash2 } from "lucide-react";
import { MedicalReport } from "@/components/icons/MedicalReport";
import { Skeleton } from "@/components/ui/Skeleton";
import { useLoadingSkeleton } from "@/shared/hooks/useLoadingSkeleton";
import { Button } from "@/components/ui/Button";
import { DataTable } from "@/components/data/DataTable";
import { Badge } from "@/components/ui/Badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/AlertDialog";
import { ApiClientError } from "@/shared/api/client";
import { env } from "@/shared/config/env";
import { toast } from "@/shared/hooks/useToast";
import { formatDateTime } from "@/shared/utils/formatters";
import {
  GridBand,
  GridBlock,
  GridCell,
} from "@/features/analytics/components/GridBlock";
import { gridPageBleedClass } from "@/features/analytics/components/gridPageBleed";
import {
  useCreateMedicalAssessment,
  useDeleteMedicalAssessment,
  useMedicalAssessmentsList,
  useSeedAleyadaDemoAssessment,
} from "./api";
import type { MedicalAssessment } from "./schemas";
import { IntakeInboxPanel } from "./components/IntakeInboxPanel";
import {
  assessmentClaimantName,
  assessmentListSubtitle,
  assessmentStatusHints,
} from "./assessmentListDisplay";

export function MedicalAssessmentsPage() {
  const navigate = useNavigate();
  const list = useMedicalAssessmentsList();
  const listSkeleton = useLoadingSkeleton("medical.assessments", list.isLoading, list.data?.length);
  const createAssessment = useCreateMedicalAssessment();
  const seedAleyadaDemo = useSeedAleyadaDemoAssessment();
  const deleteAssessment = useDeleteMedicalAssessment();
  const [deleteTarget, setDeleteTarget] = useState<MedicalAssessment | null>(null);

  const assessments = list.data ?? [];

  const onCreateBlank = async () => {
    try {
      const assessment = await createAssessment.mutateAsync({
        title: "Medical Assessment",
      });
      navigate(`/company/medical-assessment/${assessment.id}`);
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Create failed";
      toast({ title: "Could not create assessment", description: message });
    }
  };

  const onSeedAleyadaDemo = async () => {
    try {
      const assessment = await seedAleyadaDemo.mutateAsync();
      toast({
        title: "Sample case ready",
        description:
          "Full report draft loaded — edit the document, sign, then export PDF.",
      });
      navigate(`/company/medical-assessment/${assessment.id}`);
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Seed failed";
      toast({ title: "Could not load sample case", description: message });
    }
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAssessment.mutateAsync(deleteTarget.id);
      toast({ title: "Assessment deleted" });
      setDeleteTarget(null);
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Delete failed";
      toast({ title: "Could not delete assessment", description: message });
    }
  };

  const columns = useMemo<ColumnDef<MedicalAssessment>[]>(
    () => [
      {
        id: "title",
        header: "Assessment",
        cell: ({ row }) => {
          const a = row.original;
          const subtitle = assessmentListSubtitle(a);
          return (
            <div>
              <p className="font-medium">{assessmentClaimantName(a)}</p>
              {subtitle ? (
                <p className="text-xs text-muted-foreground">{subtitle}</p>
              ) : null}
            </div>
          );
        },
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: ({ row }) => {
          const a = row.original;
          const hints = assessmentStatusHints(a)
            .split(" · ")
            .slice(1)
            .join(" · ");
          return (
            <div>
              <Badge variant="secondary">{a.status}</Badge>
              {hints ? (
                <p className="mt-1 text-xs text-muted-foreground">{hints}</p>
              ) : null}
            </div>
          );
        },
      },
      {
        accessorKey: "updatedAt",
        header: "Updated",
        cell: ({ getValue }) => formatDateTime(String(getValue())),
      },
      {
        id: "actions",
        header: "",
        meta: { className: "w-12 text-right" },
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Delete assessment"
            className="text-muted-foreground hover:text-destructive"
            onClick={(e) => {
              e.stopPropagation();
              setDeleteTarget(row.original);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <div className={gridPageBleedClass(`pb-6 ${pageWithDockClass}`)}>
      <GridBlock>
        <GridBand className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold tracking-tight">
              Medical Assessment
            </h2>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              Partner inbox → record and prognosis on mobile → open the report
              here as a Word document, then sign and export.
            </p>
          </div>
        </GridBand>

        <GridCell className="lg:col-span-2">
          <IntakeInboxPanel />
        </GridCell>

        <GridBand className="flex items-center gap-2 text-sm font-medium">
          {/* DES 08-10-2026: app icon, was FileHeart */}
          <MedicalReport className="h-4 w-4 text-primary" />
          Assessments
          <span className="font-normal text-muted-foreground">
            ·{" "}
            {list.isLoading ? (
              <Skeleton className="inline-block h-3.5 w-14 align-middle" />
            ) : (
              `${assessments.length} total`
            )}
          </span>
        </GridBand>

        <GridCell padded={false} className="border-b-0">
          {/* Loading: the table's skeleton rows if there were assessments
              last time, else blank until the list or its empty state. */}
          {list.isLoading && !listSkeleton ? null : (
            <DataTable
              variant="cell"
              columns={columns}
              isLoading={list.isLoading}
              data={assessments}
              rowKey={(row) => row.id}
              onRowClick={(row) =>
                navigate(`/company/medical-assessment/${row.id}`)
              }
              emptyDescription="No assessments yet. Use Case Hub lookup or create a blank assessment."
            />
          )}
        </GridCell>
      </GridBlock>

      {/* The page's actions, docked at the bottom (DES 01-10-2026). The
          fixture case is dev/staging only: the endpoint refuses it unless
          ALLOW_DEMO_SEED is set, so prod never shows a dead button. */}
      <PageDock>
        {env.isProd ? null : (
          <button
            type="button"
            onClick={() => void onSeedAleyadaDemo()}
            disabled={seedAleyadaDemo.isPending}
            className="flex h-14 items-center gap-2 rounded-full border border-border/70 bg-background/75 px-6 text-base font-semibold text-foreground shadow-[0_8px_30px_rgba(0,0,0,0.12)] backdrop-blur-xl transition-colors hover:bg-background disabled:opacity-60"
          >
            {seedAleyadaDemo.isPending ? (
              <LoaderCircle className="h-5 w-5 animate-spin" />
            ) : (
              <FileHeart className="h-5 w-5 text-brand" strokeWidth={1.75} />
            )}
            Load sample case
          </button>
        )}
        <DockActionButton
          icon={
            createAssessment.isPending ? (
              <LoaderCircle className="h-5 w-5 animate-spin" />
            ) : (
              <Plus className="h-5 w-5" strokeWidth={2} />
            )
          }
          onClick={() => void onCreateBlank()}
          disabled={createAssessment.isPending}
        >
          Blank assessment
        </DockActionButton>
      </PageDock>

      <AlertDialog
        open={deleteTarget != null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent onClick={(e) => e.stopPropagation()}>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete assessment?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget
                ? `"${deleteTarget.title}" and its AI drafts will be removed. The linked recording session stays in Sessions.`
                : "This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteAssessment.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteAssessment.isPending}
              onClick={(e) => {
                e.preventDefault();
                void onConfirmDelete();
              }}
            >
              {deleteAssessment.isPending ? (
                <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
